import { TourStep } from "../types";

/**
 * The full registry of 28 tour steps.
 * Linear journey + branching options. The engine in Phase 3 will use these
 * + user state to decide what to show next.
 *
 * NOTE: route paths reflect the actual app routes (see App.tsx):
 *  - Dashboard: "/"
 *  - Trades:    "/trades"
 *  - Analytics: "/analytics"
 *  - Trade Assistant: "/assistant"
 *  - Market Intelligence: "/market-intelligence"
 *  - Fundamentals: "/fundamentals"
 *  - Trading Accounts: "/account/trading"
 */
export const tourStepsRegistry: TourStep[] = [
  // 0
  {
    id: "welcome",
    titleFa: "👋 خوش اومدی به JouRex!",
    titleEn: "👋 Welcome to JouRex!",
    bodyFa:
      "تو الان در حالت Mock Data هستی. ۵۰ معامله نمایشی برات گذاشتیم تا با برنامه آشنا بشی. به محض اتصال به بروکر، این داده‌ها حذف میشن.",
    bodyEn:
      "You're in Mock Data mode. We've added 50 sample trades so you can explore. Once you connect a broker, mock data is removed.",
    options: [
      { id: "start_tour", labelFa: "بزن بریم تور", labelEn: "Start the tour", goesToStepId: "dashboard_intro", navigatesTo: "/" },
      { id: "self_explore", labelFa: "بعداً، خودم می‌گردم", labelEn: "Later, I'll explore", dismisses: true },
      { id: "go_dashboard", labelFa: "فقط داشبورد رو ببینم", labelEn: "Just show me Dashboard", goesToStepId: "dashboard_intro", navigatesTo: "/" },
    ],
    nextStepId: "dashboard_intro",
    group: "Welcome",
  },
  // 1
  {
    id: "dashboard_intro",
    route: "/",
    titleFa: "📊 داشبورد عملیاتی",
    titleEn: "📊 Operational Dashboard",
    bodyFa: "این مرکز فرماندهی توئه. P&L، Equity Curve و وضعیت اکانت اینجاست.",
    bodyEn: "This is your command center. P&L, equity curve and account state live here.",
    options: [
      { id: "explain_kpis", labelFa: "KPIها رو توضیح بده", labelEn: "Explain the KPIs", goesToStepId: "dashboard_kpis" },
      { id: "explain_equity", labelFa: "Equity Curve چیه؟", labelEn: "What is Equity Curve?", goesToStepId: "dashboard_equity" },
      { id: "go_journal", labelFa: "بریم Trade Journal", labelEn: "Go to Trade Journal", goesToStepId: "journal_intro", navigatesTo: "/trades" },
    ],
    autoCompleteOnAction: "visited_dashboard",
    previousStepId: "welcome",
    nextStepId: "journal_intro",
    group: "Dashboard",
  },
  // 2
  {
    id: "dashboard_kpis",
    route: "/",
    titleFa: "📈 شاخص‌های کلیدی",
    titleEn: "📈 Key Performance Indicators",
    bodyFa: "Win Rate، Profit Factor و Avg R اعداد طلایی توئه. هرچی بالاتر بهتر.",
    bodyEn: "Win Rate, Profit Factor and Avg R are your golden numbers. Higher is better.",
    options: [
      { id: "explain_pf", labelFa: "Profit Factor یعنی چی؟", labelEn: "What is Profit Factor?", goesToStepId: "dashboard_equity" },
      { id: "next", labelFa: "بریم بعدی", labelEn: "Next", goesToStepId: "dashboard_equity" },
    ],
    previousStepId: "dashboard_intro",
    nextStepId: "dashboard_equity",
    targetId: "dashboard-kpis",
    placement: "bottom",
    group: "Dashboard",
  },
  // 3
  {
    id: "dashboard_equity",
    route: "/",
    titleFa: "📉 منحنی Equity",
    titleEn: "📉 Equity Curve",
    bodyFa: "این نمودار رشد سرمایه‌ت رو نشون میده. خط صاف بالارونده = ترید سالم.",
    bodyEn: "Tracks the growth of your capital. A smooth uptrend = healthy trading.",
    options: [
      { id: "go_journal", labelFa: "بریم Journal", labelEn: "Go to Journal", goesToStepId: "journal_intro", navigatesTo: "/trades" },
      { id: "skip", labelFa: "داشبورد کافیه", labelEn: "Dashboard is enough", dismisses: true },
    ],
    previousStepId: "dashboard_kpis",
    nextStepId: "journal_intro",
    targetId: "equity-curve",
    placement: "top",
    group: "Dashboard",
  },
  // 4
  {
    id: "journal_intro",
    route: "/trades",
    titleFa: "📔 ژورنال معاملاتی",
    titleEn: "📔 Trade Journal",
    bodyFa: "همه معاملاتت اینجا لیست شدن. ۵۰ تا معامله نمایشی می‌بینی. روی هر کدوم کلیک کن تا جزئیاتش باز بشه.",
    bodyEn: "All your trades live here. You'll see 50 sample trades. Click any row to open its detail.",
    options: [
      { id: "expand_row", labelFa: "یه معامله رو باز کن", labelEn: "Open a trade", goesToStepId: "journal_expand_row" },
      { id: "filters", labelFa: "Filterها چیکار می‌کنن؟", labelEn: "What do filters do?", goesToStepId: "journal_filters" },
      { id: "go_analytics", labelFa: "بریم Analytics", labelEn: "Go to Analytics", goesToStepId: "analytics_intro", navigatesTo: "/analytics" },
    ],
    autoCompleteOnAction: "visited_journal",
    previousStepId: "dashboard_intro",
    nextStepId: "journal_expand_row",
    targetId: "journal-table",
    placement: "top",
    group: "Journal",
  },
  // 5
  {
    id: "journal_expand_row",
    route: "/trades",
    titleFa: "🔍 جزئیات معامله",
    titleEn: "🔍 Trade Detail",
    bodyFa: "روی هر ردیف کلیک کن تا جزئیات کامل و داده‌های کیفی‌اش باز بشه.",
    bodyEn: "Click any row to open its full detail and qualitative data.",
    options: [
      { id: "done", labelFa: "انجام دادم، بعدی", labelEn: "Done, next", goesToStepId: "journal_qualitative" },
    ],
    autoCompleteOnAction: "expanded_journal_row",
    previousStepId: "journal_intro",
    nextStepId: "journal_qualitative",
    targetId: "journal-table",
    placement: "top",
    group: "Journal",
  },
  // 6
  {
    id: "journal_qualitative",
    titleFa: "✍️ داده‌های کیفی",
    titleEn: "✍️ Qualitative Data",
    bodyFa: "اینجا می‌تونی احساسات، استراتژی، اشتباهات و درس‌های هر معامله رو ثبت کنی.",
    bodyEn: "Log emotions, strategy, mistakes and lessons for each trade here.",
    options: [
      { id: "filters", labelFa: "Filterها رو ببین", labelEn: "See Filters", goesToStepId: "journal_filters", navigatesTo: "/trades" },
      { id: "go_analytics", labelFa: "بریم Analytics", labelEn: "Go to Analytics", goesToStepId: "analytics_intro", navigatesTo: "/analytics" },
    ],
    autoCompleteOnAction: "opened_qualitative_tab",
    previousStepId: "journal_expand_row",
    nextStepId: "journal_filters",
    group: "Journal",
  },
  // 7
  {
    id: "journal_filters",
    route: "/trades",
    titleFa: "🎚️ فیلترهای ژورنال",
    titleEn: "🎚️ Journal Filters",
    bodyFa: "می‌تونی بر اساس نماد، تاریخ، استراتژی یا نتیجه فیلتر بزنی.",
    bodyEn: "Filter by symbol, date, strategy or result.",
    options: [
      { id: "next", labelFa: "بریم Analytics", labelEn: "Go to Analytics", goesToStepId: "analytics_intro", navigatesTo: "/analytics" },
    ],
    autoCompleteOnAction: "applied_journal_filter",
    previousStepId: "journal_qualitative",
    nextStepId: "analytics_intro",
    targetId: "journal-filters",
    placement: "bottom",
    group: "Journal",
  },
  // 8
  {
    id: "analytics_intro",
    route: "/analytics",
    titleFa: "🧬 تحلیل پیشرفته",
    titleEn: "🧬 Advanced Analytics",
    bodyFa: "اینجا الگوهای پنهان معاملاتت کشف میشه. نقاط قوت و ضعفت رو ببین.",
    bodyEn: "Discover hidden patterns. See where you shine and where you slip.",
    options: [
      { id: "performance", labelFa: "تب Performance", labelEn: "Performance tab", goesToStepId: "analytics_performance" },
      { id: "behavior", labelFa: "تب Behavior", labelEn: "Behavior tab", goesToStepId: "analytics_behavior" },
      { id: "time", labelFa: "تب Time Analysis", labelEn: "Time tab", goesToStepId: "analytics_time" },
    ],
    autoCompleteOnAction: "visited_analytics",
    previousStepId: "journal_filters",
    nextStepId: "analytics_performance",
    targetId: "analytics-tabs",
    placement: "bottom",
    group: "Analytics",
  },
  // 9
  {
    id: "analytics_performance",
    route: "/analytics",
    titleFa: "📊 تحلیل عملکرد",
    titleEn: "📊 Performance Analysis",
    bodyFa: "Win Rate به تفکیک نماد، استراتژی و سشن. ببین کجا قوی‌تری.",
    bodyEn: "Win Rate by symbol, strategy and session. See where you're strongest.",
    options: [
      { id: "behavior", labelFa: "بریم Behavior", labelEn: "Go Behavior", goesToStepId: "analytics_behavior" },
      { id: "next", labelFa: "بریم Trade Assistant", labelEn: "Go Trade Assistant", goesToStepId: "assistant_intro", navigatesTo: "/assistant" },
    ],
    previousStepId: "analytics_intro",
    nextStepId: "analytics_behavior",
    group: "Analytics",
  },
  // 10
  {
    id: "analytics_behavior",
    route: "/analytics",
    titleFa: "🧠 تحلیل رفتاری",
    titleEn: "🧠 Behavior Analysis",
    bodyFa: "الگوهای احساسی، اشتباهات تکراری و انضباط معاملاتی‌ت اینجا تحلیل میشه.",
    bodyEn: "Emotional patterns, recurring mistakes and trading discipline.",
    options: [
      { id: "time", labelFa: "بریم Time Analysis", labelEn: "Go Time", goesToStepId: "analytics_time" },
      { id: "assistant", labelFa: "بریم Trade Assistant", labelEn: "Go Trade Assistant", goesToStepId: "assistant_intro", navigatesTo: "/assistant" },
    ],
    previousStepId: "analytics_performance",
    nextStepId: "analytics_time",
    group: "Analytics",
  },
  // 11
  {
    id: "analytics_time",
    route: "/analytics",
    titleFa: "⏰ تحلیل زمانی",
    titleEn: "⏰ Time Analysis",
    bodyFa: "بهترین ساعت‌ها و سشن‌های معاملاتی‌ت رو پیدا کن.",
    bodyEn: "Find your best trading hours and sessions.",
    options: [
      { id: "assistant", labelFa: "بریم Trade Assistant", labelEn: "Go Trade Assistant", goesToStepId: "assistant_intro", navigatesTo: "/assistant" },
    ],
    previousStepId: "analytics_behavior",
    nextStepId: "assistant_intro",
    group: "Analytics",
  },
  // 12
  {
    id: "assistant_intro",
    route: "/assistant",
    titleFa: "🎯 دستیار معاملاتی",
    titleEn: "🎯 Trade Assistant",
    bodyFa: "این مرکز اجراست. پلن‌های فعال، Risk Center و Kill Switch اینجاست.",
    bodyEn: "Your execution hub. Active plans, risk center and kill switch.",
    options: [
      { id: "active_plans", labelFa: "Active Plans چیه؟", labelEn: "What are Active Plans?", goesToStepId: "assistant_active_plans" },
      { id: "risk", labelFa: "Risk Center", labelEn: "Risk Center", goesToStepId: "assistant_risk" },
      { id: "killswitch", labelFa: "Kill Switch چیه؟", labelEn: "What is Kill Switch?", goesToStepId: "assistant_killswitch" },
      { id: "market_intel", labelFa: "بریم Market Intel", labelEn: "Go Market Intel", goesToStepId: "market_intel_intro", navigatesTo: "/market-intelligence" },
    ],
    autoCompleteOnAction: "visited_assistant",
    previousStepId: "analytics_time",
    nextStepId: "assistant_active_plans",
    group: "Assistant",
  },
  // 13
  {
    id: "assistant_active_plans",
    route: "/assistant",
    titleFa: "📋 پلن‌های فعال",
    titleEn: "📋 Active Plans",
    bodyFa: "اینجا استراتژی‌های فعالت با محدودیت ریسک نمایش داده میشن.",
    bodyEn: "Your active strategies with their risk constraints.",
    options: [
      { id: "risk", labelFa: "بریم Risk Center", labelEn: "Go Risk Center", goesToStepId: "assistant_risk" },
    ],
    previousStepId: "assistant_intro",
    nextStepId: "assistant_risk",
    targetId: "assistant-active-plans",
    placement: "bottom",
    group: "Assistant",
  },
  // 14
  {
    id: "assistant_risk",
    route: "/assistant",
    titleFa: "🛡️ مرکز ریسک",
    titleEn: "🛡️ Risk Center",
    bodyFa: "Daily DD، Max Loss و محدودیت‌های روزانه‌ت اینجا کنترل میشه.",
    bodyEn: "Daily drawdown, max loss and daily limits live here.",
    options: [
      { id: "killswitch", labelFa: "Kill Switch چیه؟", labelEn: "What is Kill Switch?", goesToStepId: "assistant_killswitch" },
      { id: "market_intel", labelFa: "بریم Market Intel", labelEn: "Go Market Intel", goesToStepId: "market_intel_intro", navigatesTo: "/market-intelligence" },
    ],
    previousStepId: "assistant_active_plans",
    nextStepId: "assistant_killswitch",
    targetId: "assistant-risk",
    placement: "right",
    group: "Assistant",
  },
  // 15
  {
    id: "assistant_killswitch",
    route: "/assistant",
    titleFa: "🚨 Kill Switch",
    titleEn: "🚨 Kill Switch",
    bodyFa: "اگر اوضاع بد بشه، این دکمه همه پوزیشن‌ها رو می‌بنده. در حالت Demo فقط شبیه‌سازیه.",
    bodyEn: "If things go south, this closes everything. In Demo mode it just simulates.",
    options: [
      { id: "market_intel", labelFa: "بریم Market Intel", labelEn: "Go Market Intel", goesToStepId: "market_intel_intro", navigatesTo: "/market-intelligence" },
    ],
    previousStepId: "assistant_risk",
    nextStepId: "market_intel_intro",
    targetId: "assistant-killswitch",
    placement: "left",
    group: "Assistant",
  },
  // 16
  {
    id: "market_intel_intro",
    route: "/market-intelligence",
    titleFa: "📡 هوش بازار",
    titleEn: "📡 Market Intelligence",
    bodyFa: "اخبار، تقویم اقتصادی، تنش‌های ژئوپلیتیک و سخنرانی مقامات اینجا جمع شده.",
    bodyEn: "News, economic calendar, geopolitical tensions and speeches — all in one place.",
    options: [
      { id: "news", labelFa: "News Feed", labelEn: "News Feed", goesToStepId: "mi_news_feed" },
      { id: "calendar", labelFa: "Calendar", labelEn: "Calendar", goesToStepId: "mi_calendar" },
      { id: "war", labelFa: "War & Tensions", labelEn: "War & Tensions", goesToStepId: "mi_war_tensions" },
      { id: "people", labelFa: "People & Speeches", labelEn: "People & Speeches", goesToStepId: "mi_people_speeches" },
    ],
    autoCompleteOnAction: "visited_market_intel",
    previousStepId: "assistant_killswitch",
    nextStepId: "mi_news_feed",
    targetId: "market-intel-tabs",
    placement: "bottom",
    group: "Market Intelligence",
  },
  // 17
  {
    id: "mi_news_feed",
    route: "/market-intelligence",
    titleFa: "📰 News Feed",
    titleEn: "📰 News Feed",
    bodyFa: "اخبار با تأثیر بازاری اینجا فیلتر شدن. روی هر کارت کلیک کن تا تحلیل کامل ببینی.",
    bodyEn: "Market-moving news, filtered. Click any card for the full analysis.",
    options: [
      { id: "drawer", labelFa: "یه خبر رو باز کن", labelEn: "Open a news card", goesToStepId: "mi_news_drawer" },
      { id: "calendar", labelFa: "بریم Calendar", labelEn: "Go to Calendar", goesToStepId: "mi_calendar" },
    ],
    previousStepId: "market_intel_intro",
    nextStepId: "mi_news_drawer",
    group: "Market Intelligence",
  },
  // 18
  {
    id: "mi_news_drawer",
    route: "/market-intelligence",
    titleFa: "🔬 جزئیات خبر",
    titleEn: "🔬 News Drawer",
    bodyFa: "این Drawer تحلیل عمقی، relevance و bias بازار رو نشون میده.",
    bodyEn: "This drawer shows deep analysis, relevance and market bias.",
    options: [
      { id: "calendar", labelFa: "بریم Calendar", labelEn: "Go to Calendar", goesToStepId: "mi_calendar" },
    ],
    autoCompleteOnAction: "opened_news_drawer",
    previousStepId: "mi_news_feed",
    nextStepId: "mi_calendar",
    group: "Market Intelligence",
  },
  // 19
  {
    id: "mi_calendar",
    route: "/market-intelligence",
    titleFa: "📅 تقویم اقتصادی",
    titleEn: "📅 Economic Calendar",
    bodyFa: "ایونت‌های مهم اقتصادی با impact، forecast و actual نمایش داده میشن.",
    bodyEn: "Key economic events with impact, forecast and actual values.",
    options: [
      { id: "war", labelFa: "War & Tensions", labelEn: "War & Tensions", goesToStepId: "mi_war_tensions" },
      { id: "people", labelFa: "People & Speeches", labelEn: "People & Speeches", goesToStepId: "mi_people_speeches" },
    ],
    previousStepId: "mi_news_drawer",
    nextStepId: "mi_war_tensions",
    group: "Market Intelligence",
  },
  // 20
  {
    id: "mi_war_tensions",
    route: "/market-intelligence",
    titleFa: "⚔️ War & Tensions",
    titleEn: "⚔️ War & Tensions",
    bodyFa: "تنش‌های ژئوپلیتیک با ۷ مرحله escalation از signal تا crisis.",
    bodyEn: "Geopolitical tensions across 7 escalation stages from signal to crisis.",
    options: [
      { id: "people", labelFa: "People & Speeches", labelEn: "People & Speeches", goesToStepId: "mi_people_speeches" },
      { id: "fundamentals", labelFa: "بریم Fundamentals", labelEn: "Go Fundamentals", goesToStepId: "fundamentals_intro", navigatesTo: "/fundamentals" },
    ],
    previousStepId: "mi_calendar",
    nextStepId: "mi_people_speeches",
    group: "Market Intelligence",
  },
  // 21
  {
    id: "mi_people_speeches",
    route: "/market-intelligence",
    titleFa: "🎤 People & Speeches",
    titleEn: "🎤 People & Speeches",
    bodyFa: "سخنرانی مقامات بانک مرکزی و وزرا، با ارزیابی hawkish/dovish.",
    bodyEn: "Central bank officials' speeches with hawkish/dovish scoring.",
    options: [
      { id: "fundamentals", labelFa: "بریم Fundamentals", labelEn: "Go Fundamentals", goesToStepId: "fundamentals_intro", navigatesTo: "/fundamentals" },
    ],
    previousStepId: "mi_war_tensions",
    nextStepId: "fundamentals_intro",
    group: "Market Intelligence",
  },
  // 22
  {
    id: "fundamentals_intro",
    route: "/fundamentals",
    titleFa: "🌍 تحلیل بنیادی",
    titleEn: "🌍 Fundamental Analysis",
    bodyFa: "تحلیل بلندمدت ارزها، طلا، نفت و شاخص‌ها بر اساس داده‌های FRED و سنتیمنت.",
    bodyEn: "Long-term analysis on currencies, gold, oil and indices via FRED + sentiment.",
    options: [
      { id: "usd", labelFa: "USD", labelEn: "USD", goesToStepId: "fundamentals_market" },
      { id: "gold", labelFa: "Gold", labelEn: "Gold", goesToStepId: "fundamentals_market" },
      { id: "oil", labelFa: "Oil", labelEn: "Oil", goesToStepId: "fundamentals_market" },
      { id: "next", labelFa: "بریم Account Setup", labelEn: "Go Account Setup", goesToStepId: "account_setup_intro", navigatesTo: "/account/trading" },
    ],
    autoCompleteOnAction: "visited_fundamentals",
    previousStepId: "mi_people_speeches",
    nextStepId: "fundamentals_market",
    group: "Fundamentals",
  },
  // 23
  {
    id: "fundamentals_market",
    route: "/fundamentals",
    titleFa: "📊 تحلیل بازار",
    titleEn: "📊 Market Detail",
    bodyFa: "macro narrative، seasonality و prediction AI برای این بازار.",
    bodyEn: "Macro narrative, seasonality and AI prediction per market.",
    options: [
      { id: "next", labelFa: "بریم Account Setup", labelEn: "Go Account Setup", goesToStepId: "account_setup_intro", navigatesTo: "/account/trading" },
    ],
    previousStepId: "fundamentals_intro",
    nextStepId: "account_setup_intro",
    group: "Fundamentals",
  },
  // 24
  {
    id: "account_setup_intro",
    route: "/account/trading",
    titleFa: "🔌 اتصال اکانت واقعی",
    titleEn: "🔌 Connect Real Account",
    bodyFa: "حالا که با برنامه آشنا شدی، می‌تونی اکانت معاملاتی‌ت رو وصل کنی. توجه: با اتصال، Mock Data حذف میشه.",
    bodyEn: "You can now connect your real broker account. Note: connecting will remove mock data.",
    options: [
      { id: "connect", labelFa: "وصل کردن MT5", labelEn: "Connect MT5", goesToStepId: "account_connect_wizard" },
      { id: "demo_trial", labelFa: "۳۰ روز رایگان دمو", labelEn: "30-day free demo", goesToStepId: "account_demo_trial" },
      { id: "later", labelFa: "بعداً", labelEn: "Later", dismisses: true },
    ],
    autoCompleteOnAction: "visited_account_setup",
    previousStepId: "fundamentals_market",
    nextStepId: "account_demo_trial",
    group: "Account Setup",
  },
  // 25
  {
    id: "account_demo_trial",
    route: "/account/trading",
    titleFa: "🎁 ۳۰ روز رایگان",
    titleEn: "🎁 30-Day Free Trial",
    bodyFa: "با اکانت دمو متاتریدر می‌تونی یک ماه کامل همه امکانات رو رایگان تجربه کنی.",
    bodyEn: "With a demo MetaTrader account, you can experience all features free for a month.",
    options: [
      { id: "wizard", labelFa: "راهنمای اتصال MT5", labelEn: "MT5 connect guide", goesToStepId: "account_connect_wizard" },
    ],
    previousStepId: "account_setup_intro",
    nextStepId: "account_connect_wizard",
    group: "Account Setup",
  },
  // 26
  {
    id: "account_connect_wizard",
    route: "/account/trading",
    titleFa: "🛠️ ویزارد اتصال",
    titleEn: "🛠️ Connect Wizard",
    bodyFa: "این ویزارد قدم به قدم بهت کمک می‌کنه EA نصب کنی و اکانتت رو وصل کنی.",
    bodyEn: "Step-by-step wizard for installing the EA and connecting your account.",
    options: [
      { id: "done", labelFa: "ممنون، فهمیدم", labelEn: "Got it, thanks", goesToStepId: "tour_completed" },
    ],
    autoCompleteOnAction: "started_connect_wizard",
    previousStepId: "account_demo_trial",
    nextStepId: "tour_completed",
    group: "Account Setup",
  },
  // 27
  {
    id: "tour_completed",
    titleFa: "🎉 تور کامل شد!",
    titleEn: "🎉 Tour Complete!",
    bodyFa: "همه چیز رو دیدی. هر وقت سوال داشتی روی دکمه راهنما کلیک کن. می‌تونی از تنظیمات تور رو بازنشانی کنی.",
    bodyEn: "You've seen it all. Click the Guide button anytime. You can reset the tour from Settings.",
    options: [
      { id: "exit", labelFa: "خروج", labelEn: "Exit", dismisses: true },
    ],
    previousStepId: "account_connect_wizard",
    group: "Complete",
  },
];

export const getStepById = (id: string) =>
  tourStepsRegistry.find((s) => s.id === id);

export const TOUR_TOTAL_STEPS = tourStepsRegistry.length;
